// Settings shared by the tools in the taskbar (Calendar, Goals, Docket, Loose
// Ends): which tools appear and in what order, how dates and times read, what
// each tool shows when it opens, and the six colors their items can take.
// Each tool's own settings live beside these, one synced key per tool.

import { REMIND_BEFORE, timeOfDay } from './docketModel'

// Docket's tasks are part of the Calendar (its Docket view), not a tool of their own.
export const TOOL_IDS = ['calendar', 'goals', 'loose-ends'] as const
export type ToolId = typeof TOOL_IDS[number]

/** The six colors a calendar event, goal, or Docket list can take. */
export const TOOL_COLORS = ['accent', 'blue', 'success', 'warning', 'error', 'muted'] as const
export type ToolColor = typeof TOOL_COLORS[number]

export const DEFAULT_TOOL_COLOR_NAMES: Record<ToolColor, string> = {
  accent: 'Accent',
  blue: 'Blue',
  success: 'Green',
  warning: 'Amber',
  error: 'Red',
  muted: 'Gray',
}

export type ToolClock = 'auto' | '12' | '24'
/** Month, day and year order; 'auto' follows the system. */
export type ToolDateOrder = 'auto' | 'mdy' | 'dmy' | 'ymd'
export type ToolDateSeparator = '/' | '-' | '.' | ' '
export type ToolAmPm = 'auto' | 'AM' | 'am' | 'a.m.'
type ToolScopeDefault = 'desk' | 'corpus' | 'last'

export interface ToolPrefs {
  /** Taskbar order, by tool id. Tools missing from it follow in their usual order. */
  order: ToolId[]
  /** Tools left out of the taskbar, the phone's tool sheet, and the tool shortcuts. */
  hidden: ToolId[]
  /** What the open-tools shortcut and the return tab open: the tool used last, or the first. */
  opens: 'last' | 'first'
  /** Weekdays (0 = Sunday) that make the weekend. */
  weekend: number[]
  clock: ToolClock
  dateOrder: ToolDateOrder
  /** Months as words ("Oct 31") or as numbers ("10/31"), wherever a date has a day. */
  dateMonths: 'words' | 'numbers'
  /** The next three shape dates written as numbers, once an order is picked; the system shapes them before that. */
  dateSeparator: ToolDateSeparator
  /** 01/05 rather than 1/5. */
  datePad: boolean
  dateYear: 'full' | 'short'
  /** "Today", "Tomorrow", "Yesterday" and the weekdays of the week ahead, in place of the date. */
  relativeDates: boolean
  /** How a 12-hour clock writes AM and PM. */
  ampm: ToolAmPm
  /** 09:00 or 9:00 on a 24-hour clock. */
  hourPad: 'auto' | 'pad' | 'none'
  /** "3 PM" rather than "3:00 PM", on a 12-hour clock. */
  trimWholeHours: boolean
  /** What a tool shows when it opens: the current desk, the whole corpus, or what it showed last. */
  scope: ToolScopeDefault
  /** The writer's names for the six colors. */
  colorNames: Record<ToolColor, string>
  /** A CSS color for each, or null to follow the theme. */
  colorValues: Record<ToolColor, string | null>
}

export const DEFAULT_TOOL_PREFS: ToolPrefs = {
  order: [...TOOL_IDS],
  hidden: [],
  opens: 'last',
  weekend: [0, 6],
  clock: 'auto',
  dateOrder: 'auto',
  dateMonths: 'words',
  dateSeparator: '/',
  datePad: false,
  dateYear: 'full',
  relativeDates: true,
  ampm: 'auto',
  hourPad: 'auto',
  trimWholeHours: false,
  scope: 'desk',
  colorNames: { ...DEFAULT_TOOL_COLOR_NAMES },
  colorValues: { accent: null, blue: null, success: null, warning: null, error: null, muted: null },
}

/** A CSS color from the color picker (hex, rgb, hsl, or oklch). */
function isColor(value: unknown): value is string {
  if (typeof value !== 'string' || !value || value.length > 64 || /[;{}<>]/.test(value)) return false
  return typeof CSS === 'undefined' || CSS.supports('color', value)
}

function oneOf<T extends string | number>(value: unknown, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? value as T : fallback
}

/** Where each order starts its numbers: ISO for year first, plain slashes otherwise. */
export function dateNumbersFor(order: ToolDateOrder): Pick<ToolPrefs, 'dateSeparator' | 'datePad' | 'dateYear'> {
  return order === 'ymd'
    ? { dateSeparator: '-', datePad: true, dateYear: 'full' }
    : { dateSeparator: '/', datePad: false, dateYear: 'full' }
}

/** The single date style that came before the order and its number options. */
function fromDateStyle(style: unknown): Partial<ToolPrefs> {
  if (style === 'monthFirst') return { dateOrder: 'mdy' }
  if (style === 'dayFirst') return { dateOrder: 'dmy' }
  if (style === 'iso') return { dateOrder: 'ymd', dateMonths: 'numbers', ...dateNumbersFor('ymd') }
  return {}
}

function weekdayList(raw: unknown, fallback: number[]): number[] {
  return Array.isArray(raw)
    ? [...new Set<number>(raw.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 6))].sort()
    : [...fallback]
}

export function normalizeToolPrefs(raw: any): ToolPrefs {
  const d = DEFAULT_TOOL_PREFS
  if (raw && raw.dateOrder === undefined && raw.dateStyle !== undefined) raw = { ...raw, ...fromDateStyle(raw.dateStyle) }
  const ids = (value: unknown) => Array.isArray(value)
    ? [...new Set(value.filter((id): id is ToolId => TOOL_IDS.includes(id)))]
    : []
  const order = ids(raw?.order)
  for (const id of TOOL_IDS) if (!order.includes(id)) order.push(id)
  const names = { ...d.colorNames }
  const values = { ...d.colorValues }
  for (const color of TOOL_COLORS) {
    const name = raw?.colorNames?.[color]
    if (typeof name === 'string' && name.trim()) names[color] = name.trim().slice(0, 24)
    const value = raw?.colorValues?.[color]
    if (isColor(value)) values[color] = value
  }
  return {
    order,
    hidden: ids(raw?.hidden),
    opens: raw?.opens === 'first' ? 'first' : 'last',
    weekend: weekdayList(raw?.weekend, d.weekend),
    clock: oneOf(raw?.clock, ['auto', '12', '24'] as const, d.clock),
    dateOrder: oneOf(raw?.dateOrder, ['auto', 'mdy', 'dmy', 'ymd'] as const, d.dateOrder),
    dateMonths: raw?.dateMonths === 'numbers' ? 'numbers' : 'words',
    dateSeparator: oneOf(raw?.dateSeparator, ['/', '-', '.', ' '] as const, d.dateSeparator),
    datePad: typeof raw?.datePad === 'boolean' ? raw.datePad : d.datePad,
    dateYear: raw?.dateYear === 'short' ? 'short' : 'full',
    relativeDates: typeof raw?.relativeDates === 'boolean' ? raw.relativeDates : d.relativeDates,
    ampm: oneOf(raw?.ampm, ['auto', 'AM', 'am', 'a.m.'] as const, d.ampm),
    hourPad: oneOf(raw?.hourPad, ['auto', 'pad', 'none'] as const, d.hourPad),
    trimWholeHours: typeof raw?.trimWholeHours === 'boolean' ? raw.trimWholeHours : d.trimWholeHours,
    scope: oneOf(raw?.scope, ['desk', 'corpus', 'last'] as const, d.scope),
    colorNames: names,
    colorValues: values,
  }
}

// ── Calendar ────────────────────────────────────────────────────────────────

type CalendarViewType = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'timeGridDays' | 'agenda' | 'tasks'

export interface CalendarLayers {
  /** Your own events. */
  events: boolean
  /** Docket tasks with a date. */
  tasks: boolean
  /** A task's deadline as a second item, when it also has a day to do it. */
  taskDeadlines: boolean
  /** Finished tasks, crossed out. */
  doneTasks: boolean
  /** Faded copies of a repeating task on the days it will come round to. */
  plannedRepeats: boolean
  /** Target goals' deadlines. */
  goalDeadlines: boolean
  /** A dot per habit met, beside the date. */
  goalDots: boolean
  /** The mark on days that met the writing habit. */
  habitMark: boolean
  /** Due dates of pages, folders, and desks. */
  dueDates: boolean
  /** Finished sprints, behind the week and day views. */
  sprints: boolean
}

export interface CalendarHeat {
  enabled: boolean
  /** What sets the four strengths: the habit's target, your recent days, or fixed numbers. 'auto' tries them in that order. */
  basis: 'auto' | 'habit' | 'history' | 'fixed'
  /** What is measured: net words or active minutes. */
  measure: 'words' | 'minutes'
  /** Fixed steps for strengths 2, 3, and 4, in words and in minutes. */
  fixedWords: [number, number, number]
  fixedMinutes: [number, number, number]
  /** How many days back "recent" reaches. */
  historyDays: number
  color: ToolColor
}

export interface CalendarPanelSections {
  writing: boolean
  goals: boolean
  tasks: boolean
  events: boolean
  /** The "Changes since" button beside each page. */
  changesSince: boolean
}

export interface CalendarPrefs {
  /** The view the calendar opens in, on today, each time it's opened; or 'last' for wherever it was left. */
  defaultView: CalendarViewType | 'last'
  /** Days in the extra view between Day and Week; 0 leaves it out. */
  daysView: 0 | 2 | 3 | 4 | 5 | 6
  /** Day view as a list of what the day holds, or as its hours. */
  dayLayout: 'list' | 'hours'
  /** What the Agenda lists, as last picked above it. */
  agendaShows: 'both' | 'events' | 'tasks'
  agendaSpan: 'week' | 'month' | 'quarter'
  showWeekends: boolean
  shadeWeekends: boolean
  weekNumbers: boolean
  /** ISO weeks (week 1 holds the year's first Thursday), or week 1 holding January 1. */
  weekNumberRule: 'local' | 'ISO'
  /** The days of the months before and after, in the month view. */
  otherMonthDays: boolean
  /** Events in the month view show when they end, not only when they start. */
  monthEventEnds: boolean
  /** Month view always shows six rows. */
  fixedWeeks: boolean
  /** Items a month cell shows before "+N more"; 0 fits as many as the cell holds. */
  maxPerDay: number
  /** First and last hours of the week and day views (0–24). */
  dayStartsAt: number
  dayEndsAt: number
  /** Hour the week and day views scroll to on opening. */
  scrollTo: number
  slotMinutes: 15 | 30 | 60
  /** Minutes a drag snaps to; 0 snaps to the slot. */
  snapMinutes: 0 | 5 | 10 | 15
  nowLine: boolean
  workHours: boolean
  workStart: number
  workEnd: number
  newEventMinutes: number
  newEventColor: ToolColor
  /** What the header's New event makes: a timed event today, or all-day on other days ('smart'). */
  headerNew: 'smart' | 'timed' | 'allDay'
  /** Dragging across empty time or days makes a new event. */
  selectToCreate: boolean
  layers: CalendarLayers
  maxGoalDots: number
  heat: CalendarHeat
  /** What clicking a date does. */
  dateClick: 'panel' | 'dayView'
  panel: CalendarPanelSections
  lockDragging: boolean
  confirmDelete: boolean
  fadePast: boolean
  /** The Calendar's keys: Alt with M, W, D, A, K, T, N on desktop (letters alone on the phone), and the arrows. */
  keys: boolean
  repeats: CalendarRepeatPrefs
  /** A writing block shows what the sprints run during it wrote. */
  writingRecord: boolean
  prep: CalendarPrepPrefs
}

export interface CalendarRepeatPrefs {
  /** Which events a change to one occurrence applies to, or 'ask'. Rule and prep changes, and deletes, always ask. */
  edit: 'ask' | 'this' | 'following' | 'all'
  /** What dragging an occurrence moves; the notice offers the other. */
  drag: 'this' | 'all'
  /** The small loop after a repeating event's title. */
  glyph: boolean
}

export interface CalendarPrepPrefs {
  /** Days before the event a new prep task starts with. */
  leadDays: number
  /** The Docket list prep tasks are made in; null is Docket (no list). */
  listId: string | null
}

export const DEFAULT_CALENDAR_PREFS: CalendarPrefs = {
  defaultView: 'timeGridDay',
  dayLayout: 'list',
  agendaShows: 'both',
  daysView: 0,
  agendaSpan: 'month',
  showWeekends: true,
  shadeWeekends: false,
  weekNumbers: false,
  weekNumberRule: 'local',
  otherMonthDays: true,
  monthEventEnds: false,
  fixedWeeks: true,
  maxPerDay: 0,
  dayStartsAt: 0,
  dayEndsAt: 24,
  scrollTo: 8,
  slotMinutes: 30,
  snapMinutes: 0,
  nowLine: true,
  workHours: false,
  workStart: 9,
  workEnd: 17,
  newEventMinutes: 60,
  newEventColor: 'accent',
  headerNew: 'smart',
  selectToCreate: true,
  layers: {
    events: true,
    tasks: true,
    taskDeadlines: true,
    doneTasks: true,
    plannedRepeats: false,
    goalDeadlines: true,
    goalDots: false,
    habitMark: true,
    dueDates: true,
    sprints: true,
  },
  maxGoalDots: 4,
  heat: {
    enabled: true,
    basis: 'auto',
    measure: 'words',
    fixedWords: [250, 500, 1000],
    fixedMinutes: [15, 30, 60],
    historyDays: 90,
    color: 'accent',
  },
  dateClick: 'panel',
  panel: { writing: true, goals: true, tasks: true, events: true, changesSince: true },
  lockDragging: false,
  confirmDelete: false,
  fadePast: true,
  keys: true,
  repeats: { edit: 'ask', drag: 'this', glyph: true },
  writingRecord: true,
  prep: { leadDays: 1, listId: null },
}

function whole(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value))
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
}

/** A set of switches, each kept from `raw` when it is a boolean. */
function flags<T extends object>(raw: any, fallback: T): T {
  const out: Record<string, boolean> = { ...fallback } as Record<string, boolean>
  for (const key of Object.keys(fallback)) {
    if (typeof raw?.[key] === 'boolean') out[key] = raw[key]
  }
  return out as T
}

/** Three rising steps, each above the last. */
function steps(raw: unknown, fallback: [number, number, number], max: number): [number, number, number] {
  if (!Array.isArray(raw) || raw.length !== 3) return [...fallback]
  const out = raw.map((value, i) => whole(value, 1, max, fallback[i])) as [number, number, number]
  out[1] = Math.max(out[1], out[0] + 1)
  out[2] = Math.max(out[2], out[1] + 1)
  return out
}

export function normalizeCalendarPrefs(raw: any): CalendarPrefs {
  const d = DEFAULT_CALENDAR_PREFS
  const dayStartsAt = whole(raw?.dayStartsAt, 0, 23, d.dayStartsAt)
  const workStart = whole(raw?.workStart, 0, 23, d.workStart)
  return {
    defaultView: oneOf(raw?.defaultView, ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'timeGridDays', 'agenda', 'tasks', 'last'] as const, d.defaultView),
    daysView: oneOf(raw?.daysView, [0, 2, 3, 4, 5, 6] as const, d.daysView),
    agendaSpan: oneOf(raw?.agendaSpan, ['week', 'month', 'quarter'] as const, d.agendaSpan),
    showWeekends: raw?.showWeekends !== false,
    shadeWeekends: raw?.shadeWeekends === true,
    weekNumbers: raw?.weekNumbers === true,
    weekNumberRule: raw?.weekNumberRule === 'ISO' ? 'ISO' : 'local',
    otherMonthDays: raw?.otherMonthDays !== false,
    monthEventEnds: raw?.monthEventEnds === true,
    fixedWeeks: raw?.fixedWeeks !== false,
    maxPerDay: whole(raw?.maxPerDay, 0, 20, d.maxPerDay),
    dayStartsAt,
    dayEndsAt: whole(raw?.dayEndsAt, dayStartsAt + 1, 24, d.dayEndsAt),
    scrollTo: whole(raw?.scrollTo, 0, 23, d.scrollTo),
    slotMinutes: oneOf(raw?.slotMinutes, [15, 30, 60] as const, d.slotMinutes),
    snapMinutes: oneOf(raw?.snapMinutes, [0, 5, 10, 15] as const, d.snapMinutes),
    nowLine: raw?.nowLine !== false,
    workHours: raw?.workHours === true,
    workStart,
    workEnd: whole(raw?.workEnd, workStart + 1, 24, Math.max(workStart + 1, d.workEnd)),
    newEventMinutes: whole(raw?.newEventMinutes, 5, 24 * 60, d.newEventMinutes),
    newEventColor: oneOf(raw?.newEventColor, TOOL_COLORS, d.newEventColor),
    headerNew: oneOf(raw?.headerNew, ['smart', 'timed', 'allDay'] as const, d.headerNew),
    selectToCreate: raw?.selectToCreate !== false,
    layers: flags(raw?.layers, d.layers),
    maxGoalDots: whole(raw?.maxGoalDots, 1, 8, d.maxGoalDots),
    heat: {
      enabled: raw?.heat?.enabled !== false,
      basis: oneOf(raw?.heat?.basis, ['auto', 'habit', 'history', 'fixed'] as const, d.heat.basis),
      measure: raw?.heat?.measure === 'minutes' ? 'minutes' : 'words',
      fixedWords: steps(raw?.heat?.fixedWords, d.heat.fixedWords, 1_000_000),
      fixedMinutes: steps(raw?.heat?.fixedMinutes, d.heat.fixedMinutes, 1_440),
      historyDays: whole(raw?.heat?.historyDays, 14, 365, d.heat.historyDays),
      color: oneOf(raw?.heat?.color, TOOL_COLORS, d.heat.color),
    },
    dateClick: raw?.dateClick === 'dayView' ? 'dayView' : 'panel',
    dayLayout: raw?.dayLayout === 'hours' ? 'hours' : 'list',
    agendaShows: oneOf(raw?.agendaShows, ['both', 'events', 'tasks'] as const, d.agendaShows),
    panel: flags(raw?.panel, d.panel),
    lockDragging: raw?.lockDragging === true,
    confirmDelete: raw?.confirmDelete === true,
    fadePast: raw?.fadePast !== false,
    repeats: {
      edit: oneOf(raw?.repeats?.edit, ['ask', 'this', 'following', 'all'] as const, d.repeats.edit),
      drag: raw?.repeats?.drag === 'all' ? 'all' : 'this',
      glyph: raw?.repeats?.glyph !== false,
    },
    writingRecord: raw?.writingRecord !== false,
    prep: {
      leadDays: whole(raw?.prep?.leadDays, 0, 60, d.prep.leadDays),
      listId: typeof raw?.prep?.listId === 'string' && raw.prep.listId ? raw.prep.listId : null,
    },
    keys: raw?.keys !== false,
  }
}

// ── Goals & Habits ──────────────────────────────────────────────────────────

export interface GoalCardLines {
  /** The current streak. */
  streak: boolean
  /** Days met so far this week, of the week's quota. */
  week: boolean
  /** The longest streak so far. */
  best: boolean
  /** The share of asked-for days met in the last 30. */
  rate: boolean
  /** The last five weeks as a grid. */
  grid: boolean
  /** A target's projected finish date. */
  projection: boolean
}

/** The goal readout's look. Whether it shows is its place on a bar (utils/barLayout). */
export interface GoalReadoutPrefs {
  /** What it shows: today's amount, a percentage, the streak, or only the ring. */
  style: 'figure' | 'percent' | 'streak' | 'ring'
  hideWhenMet: boolean
}

export interface GoalActivityPrefs {
  view: 'charts' | 'tables'
  /** Days in the words-per-day chart and the by-desk breakdown. */
  range: 7 | 14 | 30 | 90
  tiles: boolean
  daily: boolean
  year: boolean
  projects: boolean
  sprints: boolean
}

export interface SprintPrefs {
  /** The lengths offered, in minutes. */
  lengths: number[]
  defaultMinutes: number
  /** The bottom-bar clock counts down, or up. */
  clock: 'countdown' | 'elapsed'
  /** The thin progress line along the top of the window. */
  edgeLine: boolean
  /** A soft chime when time is up. */
  chime: boolean
  /** A softer chime one minute before. */
  warn: boolean
  /** At time's up, keep tallying until ended by hand. */
  overtime: boolean
  /** The summary card after a sprint. */
  summary: boolean
  /** Sprints shorter than this many seconds are not kept. */
  minSeconds: number
  /** Starts a sprint of the default length, or ends the one running. Empty for none. */
  shortcut: string
}

export interface GoalPrefs {
  /** The tab the Goals tool opens on. */
  opensOn: 'goals' | 'activity'
  showToday: boolean
  todayHideMet: boolean
  /** The goal list: the writer's order, or what still needs doing today first. */
  listOrder: 'manual' | 'today'
  showArchived: boolean
  cardStyle: 'full' | 'compact'
  cardLines: GoalCardLines
  /** Missed days forgiven each month, for habits kept every day or on set days. */
  graceDays: number
  /** Writing counts net words (added minus removed) or every word added. */
  wordsCount: 'net' | 'added'
  /** Words written in notes count, as well as in pages. */
  countNotes: boolean
  /** How far back the recent pace looks, for a target's projected finish. */
  paceWindow: 7 | 14 | 30 | 60
  /** How a target that has fallen behind says so. */
  behind: 'plain' | 'gentle' | 'hidden'
  /** What a tap on a count goal in the Today strip does. */
  countTap: 'add' | 'editor'
  /** How many days back check-ins and counts can be changed; 0 for any day. */
  backfillDays: number
  defaults: {
    measure: 'writing' | 'check' | 'count'
    words: number
    minutes: number
    writingDays: number
    otherDays: number
    color: ToolColor
    restDays: number[]
  }
  readout: GoalReadoutPrefs
  activity: GoalActivityPrefs
  sprint: SprintPrefs
}

export const DEFAULT_SPRINT_LENGTHS = [10, 15, 20, 25, 30, 45, 60]

export const DEFAULT_GOAL_PREFS: GoalPrefs = {
  opensOn: 'goals',
  showToday: true,
  todayHideMet: false,
  listOrder: 'manual',
  showArchived: false,
  cardStyle: 'full',
  cardLines: { streak: true, week: true, best: false, rate: false, grid: true, projection: true },
  graceDays: 0,
  wordsCount: 'net',
  countNotes: true,
  paceWindow: 30,
  behind: 'plain',
  countTap: 'add',
  backfillDays: 0,
  defaults: {
    measure: 'check',
    words: 500,
    minutes: 30,
    writingDays: 5,
    otherDays: 7,
    color: 'accent',
    restDays: [],
  },
  readout: { style: 'figure', hideWhenMet: false },
  activity: { view: 'charts', range: 30, tiles: true, daily: true, year: true, projects: true, sprints: true },
  sprint: {
    lengths: [...DEFAULT_SPRINT_LENGTHS],
    defaultMinutes: 25,
    clock: 'countdown',
    edgeLine: true,
    chime: false,
    warn: false,
    overtime: false,
    summary: true,
    minSeconds: 60,
    shortcut: 'Mod+Alt+Shift+G',
  },
}

const SHORTCUT = /^((Mod|Ctrl|Meta|Alt|Shift)\+)+[^+]+$/

export function normalizeGoalPrefs(raw: any): GoalPrefs {
  const d = DEFAULT_GOAL_PREFS
  const lengths = Array.isArray(raw?.sprint?.lengths)
    ? [...new Set<number>(raw.sprint.lengths.map((n: unknown) => whole(n, 1, 240, 0)).filter((n: number) => n > 0))].sort((a, b) => a - b).slice(0, 10)
    : [...d.sprint.lengths]
  const sprintLengths = lengths.length ? lengths : [...d.sprint.lengths]
  return {
    opensOn: raw?.opensOn === 'activity' ? 'activity' : 'goals',
    showToday: raw?.showToday !== false,
    todayHideMet: raw?.todayHideMet === true,
    listOrder: raw?.listOrder === 'today' ? 'today' : 'manual',
    showArchived: raw?.showArchived === true,
    cardStyle: raw?.cardStyle === 'compact' ? 'compact' : 'full',
    cardLines: flags(raw?.cardLines, d.cardLines),
    graceDays: whole(raw?.graceDays, 0, 7, d.graceDays),
    wordsCount: raw?.wordsCount === 'added' ? 'added' : 'net',
    countNotes: raw?.countNotes !== false,
    paceWindow: oneOf(raw?.paceWindow, [7, 14, 30, 60] as const, d.paceWindow),
    behind: oneOf(raw?.behind, ['plain', 'gentle', 'hidden'] as const, d.behind),
    countTap: raw?.countTap === 'editor' ? 'editor' : 'add',
    backfillDays: whole(raw?.backfillDays, 0, 365, d.backfillDays),
    defaults: {
      measure: oneOf(raw?.defaults?.measure, ['writing', 'check', 'count'] as const, d.defaults.measure),
      words: whole(raw?.defaults?.words, 1, 100_000, d.defaults.words),
      minutes: whole(raw?.defaults?.minutes, 0, 1_440, d.defaults.minutes),
      writingDays: whole(raw?.defaults?.writingDays, 1, 7, d.defaults.writingDays),
      otherDays: whole(raw?.defaults?.otherDays, 1, 7, d.defaults.otherDays),
      color: oneOf(raw?.defaults?.color, TOOL_COLORS, d.defaults.color),
      restDays: weekdayList(raw?.defaults?.restDays, d.defaults.restDays).slice(0, 6),
    },
    readout: {
      style: oneOf(raw?.readout?.style, ['figure', 'percent', 'streak', 'ring'] as const, d.readout.style),
      hideWhenMet: raw?.readout?.hideWhenMet === true,
    },
    activity: {
      ...flags(raw?.activity, { tiles: true, daily: true, year: true, projects: true, sprints: true }),
      view: raw?.activity?.view === 'tables' ? 'tables' : 'charts',
      range: oneOf(raw?.activity?.range, [7, 14, 30, 90] as const, d.activity.range),
    },
    sprint: {
      lengths: sprintLengths,
      defaultMinutes: whole(raw?.sprint?.defaultMinutes, 1, 240, d.sprint.defaultMinutes),
      clock: raw?.sprint?.clock === 'elapsed' ? 'elapsed' : 'countdown',
      edgeLine: raw?.sprint?.edgeLine !== false,
      chime: raw?.sprint?.chime === true,
      warn: raw?.sprint?.warn === true,
      overtime: raw?.sprint?.overtime === true,
      summary: raw?.sprint?.summary !== false,
      minSeconds: whole(raw?.sprint?.minSeconds, 0, 600, d.sprint.minSeconds),
      // An empty shortcut is one the writer cleared; anything unreadable gets the default.
      shortcut: raw?.sprint?.shortcut === ''
        ? ''
        : typeof raw?.sprint?.shortcut === 'string' && SHORTCUT.test(raw.sprint.shortcut) ? raw.sprint.shortcut : d.sprint.shortcut,
    },
  }
}

// ── Docket ──────────────────────────────────────────────────────────────────

export interface DocketChips {
  list: boolean
  linked: boolean
  date: boolean
  deadline: boolean
  repeat: boolean
  /**
   * A task's steps: nothing, the count done, that and the next step to do,
   * or that and every step. The count folds or unfolds each task's steps.
   */
  steps: 'none' | 'count' | 'next' | 'all'
  /** A bell on tasks with a reminder. */
  reminder: boolean
  /** The details icon, or the first line of the details under the title. */
  details: 'icon' | 'line' | 'none'
}

/** What a reminder shows besides the task's title. */
interface DocketReminderContent {
  /** The task's details: all of them (folded to their first line), the first line, or none. */
  details: 'all' | 'line' | 'none'
  /** When it is for: only when that isn't plain from the moment it goes off, always, or never. */
  when: 'auto' | 'always' | 'never'
  list: boolean
  /** The deadline, on a reminder of the day to do it. */
  deadline: boolean
  /** Steps done, as "2 of 5 steps". */
  steps: boolean
  /** The page, folder, or desk it is linked to. */
  linked: boolean
}

interface DocketReminderPrefs {
  /** 'HH:MM': when a reminder goes off for a date with no time of its own. */
  time: string
  /** Remind of every dated task, unless it is turned off on the task. */
  all: boolean
  /** Minutes ahead a reminder goes off, when every dated task reminds. */
  before: number
  content: DocketReminderContent
  /** Buttons on a reminder. */
  buttons: { done: boolean; snooze: boolean }
  /** Minutes Snooze puts a reminder off. */
  snooze: number
  /** Minutes after a reminder to remind again, if the task isn't done; 0 for never. */
  again: number
  /** A reminder stays until its task is done, instead of being swiped away. */
  keep: boolean
  /** Reminders gather into one bundle in the notification shade. */
  bundle: boolean
}

export const SNOOZE_MINUTES = [5, 10, 15, 30, 60, 120] as const
export const AGAIN_MINUTES = [0, 5, 10, 15, 30, 60, 120] as const

export interface DocketPrefs {
  /**
   * What a task's date means: the day to do it, its deadline, or either,
   * chosen on each task. Decides the fields a task shows, what a date typed
   * in the add bar sets, and which date a reminder is for.
   */
  dates: 'both' | 'do' | 'due'
  reminders: DocketReminderPrefs
  /** Where the Docket view starts: Docket itself (the list of lists on the phone), or wherever it was left on this device. */
  opensOn: 'start' | 'last'
  showLogbook: boolean
  showCounts: boolean
  /** New tasks go to the list being viewed, or always to Docket (no list). */
  newTaskTarget: 'view' | 'inbox'
  /** The add bar reads dates, deadlines and lists from what is typed. */
  quickAdd: boolean
  captureShortcut: string
  /** What happens to a ticked task: it moves into Done, waits a moment first, or stays crossed out in place. */
  finishStyle: 'move' | 'linger' | 'stay'
  undoNotice: boolean
  confirmDelete: boolean
  /** The deadline field shows on every task, or behind "Add a deadline". */
  deadlineField: 'hidden' | 'shown'
  /** Days before a deadline that a task is marked as due soon; 0 for none. */
  warnDays: number
  /** Overdue tasks, in their rows and on the calendar, and overdue due dates on the calendar. */
  overdueColor: 'warning' | 'error' | 'none'
  /** Ticking the last step finishes the task. */
  finishWithSteps: boolean
  hideDoneSteps: boolean
  density: 'comfortable' | 'compact'
  chips: DocketChips
  /** Finishes of repeating tasks are kept in the Logbook. */
  logRepeats: boolean
  /** Months the Logbook keeps; 0 keeps everything. */
  logbookMonths: number
  /** New lists belong to the desk being worked in. */
  newListInDesk: boolean
  newListColor: ToolColor
  newListChecklist: boolean
}

export const DEFAULT_DOCKET_CAPTURE = 'Mod+Alt+A'

export const DEFAULT_DOCKET_PREFS: DocketPrefs = {
  dates: 'both',
  reminders: {
    time: '09:00',
    all: false,
    before: 0,
    content: { details: 'all', when: 'auto', list: true, deadline: false, steps: false, linked: false },
    buttons: { done: false, snooze: false },
    snooze: 10,
    again: 0,
    keep: false,
    bundle: false,
  },
  opensOn: 'start',
  showLogbook: true,
  showCounts: true,
  newTaskTarget: 'view',
  quickAdd: true,
  captureShortcut: DEFAULT_DOCKET_CAPTURE,
  finishStyle: 'move',
  undoNotice: true,
  confirmDelete: false,
  deadlineField: 'hidden',
  warnDays: 0,
  overdueColor: 'warning',
  finishWithSteps: false,
  hideDoneSteps: false,
  density: 'comfortable',
  chips: { list: true, linked: true, date: true, deadline: true, repeat: true, steps: 'next', reminder: true, details: 'icon' },
  logRepeats: true,
  logbookMonths: 0,
  newListInDesk: false,
  newListColor: 'accent',
  newListChecklist: false,
}

export function normalizeDocketPrefs(raw: any): DocketPrefs {
  const d = DEFAULT_DOCKET_PREFS
  return {
    dates: oneOf(raw?.dates, ['both', 'do', 'due'] as const, d.dates),
    reminders: {
      time: timeOfDay(raw?.reminders?.time) ?? d.reminders.time,
      all: raw?.reminders?.all === true,
      before: oneOf<number>(raw?.reminders?.before, REMIND_BEFORE, d.reminders.before),
      content: {
        details: oneOf(raw?.reminders?.content?.details, ['all', 'line', 'none'] as const, d.reminders.content.details),
        when: oneOf(raw?.reminders?.content?.when, ['auto', 'always', 'never'] as const, d.reminders.content.when),
        ...flags(raw?.reminders?.content, { list: true, deadline: false, steps: false, linked: false }),
      },
      buttons: flags(raw?.reminders?.buttons, { done: false, snooze: false }),
      snooze: oneOf<number>(raw?.reminders?.snooze, SNOOZE_MINUTES, d.reminders.snooze),
      again: oneOf<number>(raw?.reminders?.again, AGAIN_MINUTES, d.reminders.again),
      keep: raw?.reminders?.keep === true,
      bundle: raw?.reminders?.bundle === true,
    },
    // Today, Upcoming and Inbox all came to mean Docket's start.
    opensOn: raw?.opensOn === 'last' ? 'last' : 'start',
    showLogbook: raw?.showLogbook !== false,
    showCounts: raw?.showCounts !== false,
    newTaskTarget: raw?.newTaskTarget === 'inbox' ? 'inbox' : 'view',
    quickAdd: raw?.quickAdd !== false,
    captureShortcut: typeof raw?.captureShortcut === 'string' && SHORTCUT.test(raw.captureShortcut) ? raw.captureShortcut : d.captureShortcut,
    finishStyle: oneOf(raw?.finishStyle, ['move', 'linger', 'stay'] as const, d.finishStyle),
    undoNotice: raw?.undoNotice !== false,
    confirmDelete: raw?.confirmDelete === true,
    deadlineField: raw?.deadlineField === 'shown' ? 'shown' : 'hidden',
    warnDays: whole(raw?.warnDays, 0, 14, d.warnDays),
    overdueColor: oneOf(raw?.overdueColor, ['warning', 'error', 'none'] as const, d.overdueColor),
    finishWithSteps: raw?.finishWithSteps === true,
    hideDoneSteps: raw?.hideDoneSteps === true,
    density: raw?.density === 'compact' ? 'compact' : 'comfortable',
    chips: {
      ...flags(raw?.chips, { list: true, linked: true, date: true, deadline: true, repeat: true, reminder: true }),
      // Once on or off; off still means nothing.
      steps: raw?.chips?.steps === false ? 'none' : oneOf(raw?.chips?.steps, ['none', 'count', 'next', 'all'] as const, d.chips.steps),
      details: oneOf(raw?.chips?.details, ['icon', 'line', 'none'] as const, d.chips.details),
    },
    logRepeats: raw?.logRepeats !== false,
    logbookMonths: oneOf(raw?.logbookMonths, [0, 3, 6, 12, 24] as const, 0),
    newListInDesk: raw?.newListInDesk === true,
    newListColor: oneOf(raw?.newListColor, TOOL_COLORS, d.newListColor),
    newListChecklist: raw?.newListChecklist === true,
  }
}
